import React from 'react';
import { Save, Loader2, Plus } from 'lucide-react';
import { Modal } from '../../../../../../../components/ui/Modal';
import type { Subtopico } from '../../../../types';

interface AulaModalProps {
  open: boolean;
  onClose: () => void;
  editingAula: Subtopico | null;
  aulaForm: {
    titulo: string;
    descricao: string;
    ordem: number;
    duracao: string;
    dificuldade: string;
    status: string;
    videoUrl: string;
    pdfUrl: string;
  };
  setAulaForm: React.Dispatch<React.SetStateAction<{
    titulo: string;
    descricao: string;
    ordem: number;
    duracao: string;
    dificuldade: string;
    status: string;
    videoUrl: string;
    pdfUrl: string;
  }>>;
  disablePdf: boolean;
  setDisablePdf: (v: boolean) => void;
  uploadingVideo: boolean;
  uploadingPdf: boolean;
  handleUpload: (e: React.ChangeEvent<HTMLInputElement>, tipo: 'VIDEO' | 'PDF') => Promise<void>;
  onSubmit: (e: React.FormEvent) => Promise<void>;
}

export function AulaModal({
  open,
  onClose,
  editingAula,
  aulaForm,
  setAulaForm,
  disablePdf,
  setDisablePdf,
  uploadingVideo,
  uploadingPdf,
  handleUpload,
  onSubmit,
}: AulaModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editingAula ? 'Editar Aula' : 'Nova Aula'}
      size="lg"
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Título da Aula</label>
            <input
              type="text"
              required
              placeholder="Ex: Como criar turmas passo a passo"
              value={aulaForm.titulo}
              onChange={(e) => setAulaForm({ ...aulaForm, titulo: e.target.value })}
              className="ecad-input-premium"
            />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Descrição / O que será ensinado</label>
            <textarea
              rows={2}
              placeholder="Resumo do conteúdo..."
              value={aulaForm.descricao}
              onChange={(e) => setAulaForm({ ...aulaForm, descricao: e.target.value })}
              className="ecad-textarea-premium"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Ordem</label>
            <input
              type="number"
              required
              value={aulaForm.ordem}
              onChange={(e) => setAulaForm({ ...aulaForm, ordem: Number(e.target.value) })}
              className="ecad-input-premium"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Duração (Texto)</label>
            <input
              type="text"
              placeholder="Ex: 5m 20s"
              value={aulaForm.duracao}
              onChange={(e) => setAulaForm({ ...aulaForm, duracao: e.target.value })}
              className="ecad-input-premium"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Dificuldade</label>
            <select
              value={aulaForm.dificuldade}
              onChange={(e) => setAulaForm({ ...aulaForm, dificuldade: e.target.value })}
              className="ecad-select-premium"
            >
              <option value="Básico">Básico</option>
              <option value="Intermediário">Intermediário</option>
              <option value="Avançado">Avançado</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Status de Visibilidade</label>
            <select
              value={aulaForm.status}
              onChange={(e) => setAulaForm({ ...aulaForm, status: e.target.value })}
              className="ecad-select-premium"
            >
              <option value="PUBLICADO">PUBLICADO (Visível)</option>
              <option value="RASCUNHO">RASCUNHO (Oculto)</option>
              <option value="INATIVO">INATIVO (Oculto)</option>
            </select>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>URL do Vídeo (Link manual mp4/webm)</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                placeholder="https://gcs-url/video.mp4"
                value={aulaForm.videoUrl}
                onChange={(e) => setAulaForm({ ...aulaForm, videoUrl: e.target.value })}
                className="ecad-input-premium"
                style={{ flex: 1 }}
              />
              <label style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '0 14px',
                height: 38,
                borderRadius: 8,
                border: '1px solid var(--border-color, #cbd5e1)',
                background: 'var(--bg-main, #f8fafc)',
                color: 'var(--text-main, #334155)',
                fontSize: '.8rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }} className="hover-upload">
                {uploadingVideo ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Plus size={14} />
                )}
                <span>Upload</span>
                <input
                  type="file"
                  accept="video/mp4,video/webm"
                  onChange={(e) => handleUpload(e, 'VIDEO')}
                  style={{ display: 'none' }}
                  disabled={uploadingVideo}
                />
              </label>
            </div>
          </div>
          <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>URL do PDF (Manual de Treinamento)</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.75rem', color: 'var(--text-main)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={disablePdf}
                  onChange={(e) => {
                    setDisablePdf(e.target.checked);
                    if (e.target.checked) {
                      setAulaForm(prev => ({ ...prev, pdfUrl: '' }));
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                />
                <span>Desativar PDF para esta aula</span>
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                placeholder={disablePdf ? "PDF desativado nesta aula" : "https://gcs-url/manual.pdf"}
                value={aulaForm.pdfUrl}
                onChange={(e) => setAulaForm({ ...aulaForm, pdfUrl: e.target.value })}
                className="ecad-input-premium"
                style={{ flex: 1 }}
                disabled={disablePdf}
              />
              <label style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '0 14px',
                height: 38,
                borderRadius: 8,
                border: '1px solid var(--border-color, #cbd5e1)',
                background: 'var(--bg-main, #f8fafc)',
                color: 'var(--text-main, #334155)',
                fontSize: '.8rem',
                fontWeight: 600,
                cursor: disablePdf ? 'not-allowed' : 'pointer',
                opacity: disablePdf ? 0.6 : 1,
                transition: 'all 0.2s'
              }} className="hover-upload">
                {uploadingPdf ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Plus size={14} />
                )}
                <span>Upload</span>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => handleUpload(e, 'PDF')}
                  style={{ display: 'none' }}
                  disabled={uploadingPdf || disablePdf}
                />
              </label>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
          <button type="button" onClick={onClose} className="btn-cancel" style={{ height: 36, padding: '0 16px', fontSize: '.8rem', borderRadius: 8 }}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6, height: 36, padding: '0 16px', fontSize: '.8rem', borderRadius: 8 }}>
            <Save size={14} /> Salvar Aula
          </button>
        </div>
      </form>
    </Modal>
  );
}
